import { addYears } from 'date-fns';
import {
    BodyMediumRegular,
    H5Regular,
    LoadingSkeleton,
    XStack,
    YStack,
} from 'smoothie';

import Logo from '../../../assets/images/logos/fruitful-logo-icon-only.svg';
import {
    format,
    SHORT_READABLE_DATE_FORMAT,
} from '../../common/utils/dateUtils';

interface SummaryRowProps {
    label: string;
    value: string;
    isLoading: boolean;
}

const SummaryRow = ({ label, value, isLoading }: SummaryRowProps) => (
    <XStack justifyContent="space-between" alignItems="center" width="100%">
        <BodyMediumRegular color="$gray1000">{label}</BodyMediumRegular>
        {isLoading ? (
            <LoadingSkeleton width={90} height={14} />
        ) : (
            <BodyMediumRegular>{value}</BodyMediumRegular>
        )}
    </XStack>
);

interface PremiumHeroProps {
    isLoading: boolean;
    /** Always Yearly's price — a quarterly plan is still a 12-month commitment. */
    annualCommitmentText: string;
}

export const PremiumHero = ({
    isLoading,
    annualCommitmentText,
}: PremiumHeroProps) => (
    <YStack
        gap={4}
        padding={20}
        borderRadius={24}
        backgroundColor="$peachLight"
        alignItems="center"
    >
        <Logo height={28} />
        <BodyMediumRegular color="$peachDarkest">
            Fruitful Premium Membership
        </BodyMediumRegular>
        {isLoading ? (
            <LoadingSkeleton width={140} height={20} marginVertical={4} />
        ) : (
            <H5Regular color="$peachDarkest">{annualCommitmentText}</H5Regular>
        )}
    </YStack>
);

interface PlanSummaryProps {
    isLoading: boolean;
    /** Always Yearly's price — a quarterly plan is still a 12-month commitment. */
    annualCommitmentText: string;
    dueTodayText: string;
}

const PlanSummary = ({
    isLoading,
    annualCommitmentText,
    dueTodayText,
}: PlanSummaryProps) => {
    const renewalDateText = format(
        addYears(new Date(), 1),
        SHORT_READABLE_DATE_FORMAT,
    );

    return (
        <YStack
            gap={8}
            padding={16}
            borderRadius={24}
            backgroundColor="$gray100"
            width="100%"
        >
            <SummaryRow
                label="Annual commitment"
                value={annualCommitmentText}
                isLoading={isLoading}
            />
            <SummaryRow
                label="Renews"
                value={renewalDateText}
                isLoading={false}
            />
            <SummaryRow
                label="Due today"
                value={dueTodayText}
                isLoading={isLoading}
            />
        </YStack>
    );
};

export default PlanSummary;
